// pages/support/tickets/TicketsPage.tsx
import { useState, useMemo } from "react";
import {
  Plus, Search, Filter, X, Eye, Edit, Trash2, User, ChevronDown, ChevronRight, Tags,
  MoreVertical, FileText, RefreshCw, Clock, AlertTriangle, CheckCircle, XCircle,
  RotateCcw, ArrowRight, Calendar, Users, List
} from "lucide-react";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import MenuModal, { Menu } from "../../../components/ui/MenuModal";
import { Ticket } from "../../../types/features/Ticket";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import PageListLayout from "../../../layouts/PageListLayout";
import useTickets from "../../../hooks/features/tickets/useTickets";
import TicketList from "../../../components/features/tickets/TicketsList";
import TicketSaveModal from "../../../components/features/tickets/TicketSaveModal";
import TicketDetailsModal from "../../../components/features/tickets/TicketDetailsModal";
import { useNavigate } from "react-router-dom";
import useAuth from "../../../hooks/features/auth/useAuth";
import { ticketService } from "../../../services/ticketService";

// Statuts possibles avec leurs libellés et couleurs
const STATUS_OPTIONS = {
  nouveau: { label: "Nouveau", color: "bg-blue-100 text-blue-700", order: 0, icon: FileText },
  en_cours: { label: "En cours", color: "bg-purple-100 text-purple-700", order: 1, icon: RefreshCw },
  en_attente: { label: "En attente", color: "bg-yellow-100 text-yellow-700", order: 2, icon: Clock },
  escalade: { label: "Escalade", color: "bg-red-100 text-red-700", order: 3, icon: AlertTriangle },
  resolu: { label: "Résolu", color: "bg-green-100 text-green-700", order: 4, icon: CheckCircle },
  ferme: { label: "Fermé", color: "bg-gray-100 text-gray-700", order: 5, icon: XCircle },
};

// Règles de transition
const STATUS_TRANSITIONS: Record<Ticket["status"], Ticket["status"][]> = {
  nouveau: ["en_cours", "escalade"],
  en_cours: ["en_attente", "escalade", "resolu"],
  en_attente: ["en_cours", "escalade"],
  escalade: ["en_cours", "resolu"],
  resolu: ["ferme"],
  ferme: [],
};

const STATUS_ICONS: Record<Ticket["status"], any> = {
  nouveau: FileText,
  en_cours: RefreshCw,
  en_attente: Clock,
  escalade: AlertTriangle,
  resolu: CheckCircle,
  ferme: XCircle,
};

const PERIODS = [
  { value: "today", label: "Aujourd'hui" },
  { value: "yesterday", label: "Hier" },
  { value: "week", label: "Cette semaine" },
  { value: "month", label: "Ce mois-ci" },
  { value: "custom", label: "Personnalisé" },
];

export default function TicketsPage() {
  const navigate = useNavigate();
  const { tickets, updateTicket, deleteTicket, loading, pagination, fetchTickets } = useTickets();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPriority, setSelectedPriority] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());
  const [groupByClient, setGroupByClient] = useState(false); // 👈 État pour le regroupement
  const { user } = useAuth();
  console.log(tickets)
  const [selectedPeriod, setSelectedPeriod] = useState<string>("today");
  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");

  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [ticketToView, setTicketToView] = useState<Ticket | null>(null);
  const [ticketToEdit, setTicketToEdit] = useState<Ticket | null>(null);
  const [ticketToDelete, setTicketToDelete] = useState<Ticket | null>(null);
  const [ticketToChangeStatus, setTicketToChangeStatus] = useState<{ ticket: Ticket; status: Ticket["status"] } | null>(null);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [itemsToDelete, setItemsToDelete] = useState<string[] | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isChangingStatus, setIsChangingStatus] = useState(false);

  const filterByPeriod = (ticket: Ticket) => {
    const date = new Date(ticket.created_at);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());

    const startOfMonth = new Date(today);
    startOfMonth.setDate(1);

    switch (selectedPeriod) {
      case "today": return date >= today;
      case "yesterday": return date >= yesterday && date < today;
      case "week": return date >= startOfWeek;
      case "month": return date >= startOfMonth;
      case "custom":
        const start = new Date(customStartDate);
        const end = new Date(customEndDate);
        end.setHours(23, 59, 59, 999);
        if (customStartDate && customEndDate) {
          return date >= start && date <= end;
        }
        return true;
      default: return true;
    }
  };

  const filteredTickets = useMemo(() => {
    let result = tickets;
    result = result.filter(filterByPeriod);

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(
        (t) => t.title.toLowerCase().includes(searchLower) || t.client_name?.toLowerCase().includes(searchLower)
      );
    }

    if (selectedPriority) {
      result = result.filter((t) => t.priority === selectedPriority);
    }

    if (selectedStatus) {
      result = result.filter((t) => t.status === selectedStatus);
    }

    return result;
  }, [tickets, searchTerm, selectedPriority, selectedStatus, selectedPeriod, customStartDate, customEndDate]);

  // Regroupement par client
  const groupedTickets = useMemo(() => {
    if (!groupByClient) return null;

    const groups: { [clientId: string]: { clientName: string; tickets: Ticket[]; lastTicketDate: Date } } = {};

    filteredTickets.forEach((ticket) => {
      const clientId = ticket.client_id || "no-client";
      const clientName = ticket.client_name || "Client inconnu";
      const ticketDate = new Date(ticket.created_at);

      if (!groups[clientId]) {
        groups[clientId] = { clientName, tickets: [], lastTicketDate: ticketDate };
      }
      groups[clientId].tickets.push(ticket);

      if (ticketDate > groups[clientId].lastTicketDate) {
        groups[clientId].lastTicketDate = ticketDate;
      }
    });

    return groups;
  }, [filteredTickets, groupByClient]);

  const sortedClientIds = useMemo(() => {
    if (!groupedTickets) return [];
    return Object.keys(groupedTickets).sort((a, b) => {
      const dateA = groupedTickets[a].lastTicketDate;
      const dateB = groupedTickets[b].lastTicketDate;
      return dateB.getTime() - dateA.getTime();
    });
  }, [groupedTickets]);

  const toggleClient = (clientId: string) => {
    setExpandedClients((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(clientId)) {
        newSet.delete(clientId);
      } else {
        newSet.add(clientId);
      }
      return newSet;
    });
  };

  const toggleAllClients = () => {
    if (!groupedTickets) return;
    const clientIds = Object.keys(groupedTickets);
    if (expandedClients.size === clientIds.length) {
      setExpandedClients(new Set());
    } else {
      setExpandedClients(new Set(clientIds));
    }
  };

  const stats = {
    total: tickets.length,
    nouveau: tickets.filter((t) => t.status === "nouveau").length,
    en_cours: tickets.filter((t) => t.status === "en_cours").length,
    en_attente: tickets.filter((t) => t.status === "en_attente").length,
    escalade: tickets.filter((t) => t.status === "escalade").length,
    resolu: tickets.filter((t) => t.status === "resolu").length,
    ferme: tickets.filter((t) => t.status === "ferme").length,
  };

  const handleStatusChange = async (id: string, status: Ticket["status"]) => {
    setIsChangingStatus(true);
    const success = await updateTicket(id, { status });
    setIsChangingStatus(false);
    if (success) {
      const statusLabels: Record<Ticket["status"], string> = {
        nouveau: "Nouveau",
        en_cours: "En cours",
        en_attente: "En attente",
        escalade: "Escalade",
        resolu: "Résolu",
        ferme: "Fermé",
      };
      alertSuccess(`Ticket passé en statut "${statusLabels[status]}"`);
      setTicketToChangeStatus(null);
      setTicketToView(null);
      setDetailsModalOpen(false);
      fetchTickets(currentPage, pagination.perPage);
    } else {
      alertError("Erreur lors du changement de statut");
    }
  };

  const handleDelete = async () => {
    if (!ticketToDelete) return;
    setIsDeleting(true);
    const success = await deleteTicket(ticketToDelete.id);
    setIsDeleting(false);
    if (success) {
      setTicketToDelete(null);
      alertSuccess("Ticket supprimé avec succès");
      fetchTickets(currentPage, pagination.perPage);
    } else {
      alertError("Erreur lors de la suppression");
    }
  };

  const handleDeleteMany = async () => {
    if (!itemsToDelete || itemsToDelete.length === 0) return;
    let success = true;
    for (const id of itemsToDelete) {
      const result = await deleteTicket(id);
      if (!result) success = false;
    }
    if (success) {
      setItemsToDelete(null);
      setSelectedIds([]);
      alertSuccess(`${itemsToDelete.length} ticket(s) supprimé(s)`);
      fetchTickets(currentPage, pagination.perPage);
    } else {
      alertError("Erreur lors de la suppression multiple");
    }
  };

  const clearFilters = () => {
    setSelectedPriority("");
    setSelectedStatus("");
    setSearchTerm("");
    setSelectedPeriod("today");
    setCustomStartDate("");
    setCustomEndDate("");
  };

  const hasActiveFilters = selectedPriority !== "" || selectedStatus !== "" || searchTerm.trim() !== "";

  const getStatusMenu = (ticket: Ticket): Menu[] => {
    const currentStatus = ticket.status;
    const allowedTransitions = STATUS_TRANSITIONS[currentStatus] || [];

    if (allowedTransitions.length === 0) {
      return [{ label: "Aucun changement possible", icon: XCircle, onClick: () => { } }];
    }

    return allowedTransitions.map((status) => {
      const Icon = STATUS_ICONS[status] || ArrowRight;
      return {
        label: `Passer en "${STATUS_OPTIONS[status].label}"`,
        icon: Icon,
        onClick: () => setTicketToChangeStatus({ ticket, status }),
      };
    });
  };

  const getActionsMenu = (ticket: Ticket): Menu[] => {
    const menu: Menu[] = [
      {
        label: "Voir détails",
        icon: Eye,
        onClick: () => {
          navigate(`details/${ticket.id}`)
          setDetailsModalOpen(true);
          setSelectedTicket(null);
        },
      },
      {
        label: "Modifier",
        icon: Edit,
        onClick: () => {
          setTicketToEdit(ticket);
          setModalOpen(true);
          setSelectedTicket(null);
        },
      },
    ];

    const statusSubMenu = getStatusMenu(ticket);
    if (statusSubMenu.length > 0 && statusSubMenu[0].label !== "Aucun changement possible") {
      menu.push({
        label: "Changer le statut",
        icon: RotateCcw,
        subMenu: statusSubMenu,
      });
    }

    if (ticket.status !== "ferme") {
      menu.push({
        label: "Supprimer",
        icon: Trash2,
        onClick: () => {
          setTicketToDelete(ticket);
          setSelectedTicket(null);
        },
      });
    }

    return menu;
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchTickets(page, pagination.perPage, {
      status: selectedStatus || undefined,
      priority: selectedPriority || undefined,
    });
  };

  const renderClientGroup = (clientId: string, clientName: string, clientTickets: Ticket[]) => {
    const isExpanded = expandedClients.has(clientId);

    const statusCounts = clientTickets.reduce((acc, ticket) => {
      acc[ticket.status] = (acc[ticket.status] || 0) + 1;
      return acc;
    }, {} as Record<Ticket["status"], number>);

    const statusesToShow = Object.keys(statusCounts)
      .filter((status) => statusCounts[status as Ticket["status"]] > 0)
      .sort((a, b) => STATUS_OPTIONS[a as Ticket["status"]]?.order - STATUS_OPTIONS[b as Ticket["status"]]?.order);

    const lastTicket = clientTickets.length > 0
      ? clientTickets.reduce((latest, current) => {
        const latestDate = new Date(latest.created_at);
        const currentDate = new Date(current.created_at);
        return currentDate > latestDate ? current : latest;
      })
      : null;

    const lastTicketDate = lastTicket ? new Date(lastTicket.created_at) : null;

    const activeTickets = clientTickets.filter(
      (t) => t.status === "nouveau" || t.status === "en_cours" || t.status === "en_attente"
    );
    const firstActiveTicket = activeTickets.length > 0 ? activeTickets[0] : null;

    return (
      <div key={clientId} className="border border-gray-200 rounded-xl overflow-hidden mb-3">
        <div
          onClick={() => toggleClient(clientId)}
          className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
        >
          <div className="flex items-center gap-3">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400" />
            )}
            <User className="w-4 h-4 text-gray-400" />
            <div>
              <span className="font-medium text-gray-900">{clientName}</span>
              <span className="text-sm text-gray-500 ml-2">
                ({clientTickets.length} ticket{clientTickets.length > 1 ? "s" : ""})
              </span>
              {firstActiveTicket && firstActiveTicket.position !== null && (
                <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                  Position : #{firstActiveTicket.position}
                </span>
              )}
              {lastTicket && <p className="text-xs text-gray-400 mt-0.5">Dernier ticket : {lastTicket.title}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {statusesToShow.map((status) => {
              const count = statusCounts[status as Ticket["status"]];
              const colorClass = STATUS_OPTIONS[status as Ticket["status"]]?.color || "bg-gray-100 text-gray-700";
              const label = STATUS_OPTIONS[status as Ticket["status"]]?.label || status;
              return (
                <span key={status} className={`px-2 py-0.5 text-xs font-medium rounded-full ${colorClass}`}>
                  {label} {count}
                </span>
              );
            })}
            {lastTicketDate && (
              <span className="text-xs text-gray-400 ml-2">
                {lastTicketDate.toLocaleDateString("fr-FR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            )}
          </div>
        </div>

        {isExpanded && (
          <div className="p-2">
            <TicketList
              tickets={clientTickets}
              onAction={(ticket) => setSelectedTicket(ticket)}
              onSelectTickets={(selected) => setSelectedIds(selected.map((t) => t.id))}
              selectable={true}
              selectActions={[
                {
                  label: "Supprimer",
                  onClick: () => setItemsToDelete(selectedIds),
                  className: "bg-red-600 text-white",
                },
              ]}
            />
          </div>
        )}
      </div>
    );
  };

  const clientIds = groupedTickets ? Object.keys(groupedTickets) : [];

  return (
    <PageListLayout title="">
      <div className="px-6 py-4 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Tickets</h1>
              <p className="text-sm text-gray-500 mt-1">Gérez les réclamations et demandes clients</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => navigate(`/${user?.role}/tickets/categories-problem`)}
                className="flex items-center justify-center gap-2 px-4 py-2 text-sm bg-purple-100 text-purple-700 rounded-xl hover:bg-purple-200 transition-colors"
              >
                <Tags size={18} />
                Gérer les catégories
              </button>

              <button
                onClick={async () => {
                  try {
                    const result = await ticketService.recalculatePositions();
                    alertSuccess(result.message);
                    fetchTickets(currentPage, pagination.perPage);
                  } catch (error) {
                    alertError("Erreur lors du recalcul des positions");
                  }
                }}
                className="flex items-center justify-center gap-2 px-4 py-2 text-sm bg-blue-100 text-blue-700 rounded-xl hover:bg-blue-200 transition-colors"
              >
                <RefreshCw size={18} />
                Reorganiser la file
              </button>

              {/* Bouton pour activer/désactiver le regroupement par client */}
              <button
                onClick={() => {
                  setGroupByClient(!groupByClient);
                  setExpandedClients(new Set());
                }}
                className={`flex items-center justify-center gap-2 px-4 py-2 text-sm rounded-xl transition-colors ${groupByClient
                    ? "bg-indigo-600 text-white hover:bg-indigo-700"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
              >
                {groupByClient ? <Users size={18} /> : <List size={18} />}
                {groupByClient ? "Vue groupée" : "Vue liste"}
              </button>

              <button
                onClick={() => {
                  setTicketToEdit(null);
                  setModalOpen(true);
                }}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-xl hover:bg-green-700"
              >
                <Plus size={18} />
                Nouveau ticket
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mt-6">
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-3">
              <p className="text-xs text-gray-700/70">Total</p>
              <p className="text-xl font-bold text-gray-700">{stats.total}</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-3">
              <p className="text-xs text-blue-700/70">Nouveaux</p>
              <p className="text-xl font-bold text-blue-700">{stats.nouveau}</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-3">
              <p className="text-xs text-purple-700/70">En cours</p>
              <p className="text-xl font-bold text-purple-700">{stats.en_cours}</p>
            </div>
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-3">
              <p className="text-xs text-yellow-700/70">En attente</p>
              <p className="text-xl font-bold text-yellow-700">{stats.en_attente}</p>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-3">
              <p className="text-xs text-red-700/70">Escalade</p>
              <p className="text-xl font-bold text-red-700">{stats.escalade}</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-3">
              <p className="text-xs text-green-700/70">Résolus</p>
              <p className="text-xl font-bold text-green-700">{stats.resolu + stats.ferme}</p>
            </div>
          </div>

          {/* Période */}
          <div className="flex items-center gap-3 mt-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Calendar size={18} className="text-gray-400" />
              <span className="text-sm font-medium text-gray-700">Période :</span>
            </div>
            <div className="flex gap-1 flex-wrap">
              {PERIODS.map((period) => (
                <button
                  key={period.value}
                  onClick={() => {
                    setSelectedPeriod(period.value);
                    if (period.value !== "custom") {
                      setCustomStartDate("");
                      setCustomEndDate("");
                    }
                  }}
                  className={`px-3 py-1 text-sm rounded-lg transition-colors ${selectedPeriod === period.value
                      ? "bg-primary text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                >
                  {period.label}
                </button>
              ))}
            </div>

            {selectedPeriod === "custom" && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                />
                <span className="text-sm text-gray-500">à</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                />
              </div>
            )}
          </div>

          {/* Recherche */}
          <div className="relative mt-4">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un ticket..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500/50"
            />
          </div>

          {/* Filtres */}
          <div className="mt-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${showFilters ? "bg-green-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
            >
              <Filter size={18} /> Filtrer
              {hasActiveFilters && <span className="w-2 h-2 bg-red-500 rounded-full" />}
            </button>

            {showFilters && (
              <div className="bg-gray-50 rounded-xl p-4 mt-3 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-700">Filtres</h3>
                  {hasActiveFilters && (
                    <button onClick={clearFilters} className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1">
                      <X size={14} /> Effacer tout
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priorité</label>
                    <select
                      value={selectedPriority}
                      onChange={(e) => setSelectedPriority(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">Toutes</option>
                      <option value="basse">Basse</option>
                      <option value="moyenne">Moyenne</option>
                      <option value="haute">Haute</option>
                      <option value="critique">Critique</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">Tous</option>
                      {Object.entries(STATUS_OPTIONS).map(([key, { label }]) => (
                        <option key={key} value={key}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Liste des tickets */}
      <div className="px-6 py-8 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        ) : filteredTickets.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Aucun ticket trouvé pour cette période</p>
          </div>
        ) : groupByClient ? (
          // Vue groupée par client
          <>
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm text-gray-500">
                {filteredTickets.length} ticket(s) regroupé(s) par client
              </span>
              {clientIds.length > 0 && (
                <button
                  onClick={toggleAllClients}
                  className="text-sm text-indigo-600 hover:text-indigo-700"
                >
                  {expandedClients.size === clientIds.length ? "Réduire tout" : "Développer tout"}
                </button>
              )}
            </div>
            <div className="space-y-3">
              {sortedClientIds.map((clientId) => {
                const { clientName, tickets: clientTickets } = groupedTickets![clientId];
                return renderClientGroup(clientId, clientName, clientTickets);
              })}
            </div>
          </>
        ) : (
          // Vue liste plate
          <>
            <div className="mb-4">
              <span className="text-sm text-gray-500">
                {filteredTickets.length} ticket(s) affiché(s)
              </span>
            </div>
            <TicketList
              tickets={filteredTickets}
              onAction={(ticket) => setSelectedTicket(ticket)}
              onSelectTickets={(selected) => setSelectedIds(selected.map((t) => t.id))}
              selectable={true}
              selectActions={[
                {
                  label: "Supprimer",
                  onClick: () => setItemsToDelete(selectedIds),
                  className: "bg-red-600 text-white",
                },
              ]}
            />
          </>
        )}

        {pagination.total > 0 && (
          <div className="flex justify-between items-center mt-6">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 text-gray-600 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Précédent
            </button>
            <span className="text-sm text-gray-600">
              Page {currentPage} sur {pagination.lastPage} ({pagination.total} tickets)
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === pagination.lastPage}
              className="px-4 py-2 text-gray-600 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Suivant
            </button>
          </div>
        )}
      </div>

      {selectedTicket && (
        <MenuModal
          title={selectedTicket.title}
          isOpen={true}
          onClose={() => setSelectedTicket(null)}
          icon={null}
          menu={getActionsMenu(selectedTicket)}
        />
      )}

      {ticketToChangeStatus && (
        <DeleteConfirmationModal
          isOpen={true}
          onClose={() => setTicketToChangeStatus(null)}
          onConfirm={() => handleStatusChange(ticketToChangeStatus.ticket.id, ticketToChangeStatus.status)}
          title="Changer le statut"
          message={`Passer le ticket "${ticketToChangeStatus.ticket.title}" en "${STATUS_OPTIONS[ticketToChangeStatus.status].label}" ?`}
          confirmText={isChangingStatus ? "Changement..." : "Confirmer"}
          cancelText="Annuler"
        />
      )}

      <TicketSaveModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setTicketToEdit(null);
        }}
        onSuccess={() => {
          setModalOpen(false);
          alertSuccess(ticketToEdit ? "Ticket modifié" : "Ticket créé");
          setTicketToEdit(null);
          fetchTickets(currentPage, pagination.perPage);
        }}
        initialData={ticketToEdit || undefined}
      />

      <TicketDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setTicketToView(null);
        }}
        ticket={ticketToView}
        onStatusChange={handleStatusChange}
      />

      <DeleteConfirmationModal
        isOpen={!!ticketToDelete}
        onClose={() => setTicketToDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer le ticket"
        message={`Supprimer le ticket "${ticketToDelete?.title}" ?`}
        confirmText={isDeleting ? "Suppression..." : "Supprimer"}
      />

      <DeleteConfirmationModal
        isOpen={!!itemsToDelete && itemsToDelete.length > 0}
        onClose={() => setItemsToDelete(null)}
        onConfirm={handleDeleteMany}
        title="Suppression multiple"
        message={`Supprimer ${itemsToDelete?.length} ticket(s) ?`}
      />
    </PageListLayout>
  );
}